import { useState } from 'react';
import { Settings, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { SITE_SHORT_NAME } from '@/config/site';
import { useSettings } from '@/hooks/useProducts';

interface MaintenancePageProps {
  title?: string;
  message?: string;
  estimatedEnd?: string | null;
}

/**
 * The "we're offline" screen every non-admin visitor sees while maintenance
 * mode is on — for several tenants it is the ONLY page the public can reach,
 * so it carries the brand rather than a generic placeholder.
 *
 * Palette, fonts and favicon already arrive on their own: RuntimeBrand is
 * mounted ABOVE MaintenanceGuard in App.tsx, so the applied kit's CSS vars
 * (--color-sage-*, --font-display) are live here even though no layout wraps
 * this page. What was missing is the logo — `store_logo_url` /
 * `store_logo_dark_url`, both public settings written by Branding Studio's
 * "Apply to site", so no auth is needed to read them.
 */
export default function MaintenancePage({
  title = "We'll Be Right Back",
  message = "We're currently performing scheduled maintenance. Please check back shortly.",
  estimatedEnd,
}: MaintenancePageProps) {
  const { data: settingsData } = useSettings();
  const settings = settingsData?.settings;

  const storeName = (settings?.store_name as string | undefined) || SITE_SHORT_NAME;

  // Same contract as the site header: a CSS-swapped sibling pair (no JS theme
  // state), falling back to the Lucide mark if the image 404s or no kit has
  // been applied. `hidden` is display:none, so the inactive logo leaves the
  // accessibility tree and the name isn't announced twice.
  const [logoOk, setLogoOk] = useState(true);
  const logoUrl = logoOk ? (settings?.store_logo_url as string | undefined) : undefined;
  const logoDarkUrl = logoOk
    ? (settings?.store_logo_dark_url as string | undefined)
    : undefined;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-surface-soft to-sage-50 px-4">
      <div className="text-center max-w-lg">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {logoUrl ? (
            <>
              <img
                src={logoUrl}
                alt={storeName}
                onError={() => setLogoOk(false)}
                className={cn(
                  'h-12 sm:h-14 w-auto max-w-[70vw] object-contain',
                  logoDarkUrl && 'dark:hidden',
                )}
              />
              {logoDarkUrl && (
                <img
                  src={logoDarkUrl}
                  alt={storeName}
                  className="hidden h-12 sm:h-14 w-auto max-w-[70vw] object-contain dark:block"
                />
              )}
            </>
          ) : (
            <>
              <Store className="w-8 h-8 text-sage-700" />
              <span className="text-2xl font-bold text-ink tracking-tight">{storeName}</span>
            </>
          )}
        </div>

        {/* Spinning gear icon */}
        <div className="mb-6">
          <Settings
            className="w-16 h-16 text-sage-600 mx-auto animate-spin"
            style={{ animationDuration: '4s' }}
          />
        </div>

        {/* Title & message */}
        <h1 className="font-display text-3xl font-bold text-ink mb-3">{title}</h1>
        <p className="text-lg text-ink-soft mb-6">{message}</p>

        {/* Estimated end */}
        {estimatedEnd && (
          <p className="text-sm text-ink-muted mb-8">
            Estimated return:{' '}
            <time className="font-medium text-ink-soft">
              {new Date(estimatedEnd).toLocaleString()}
            </time>
          </p>
        )}

        {/* Admin link */}
        <p className="text-sm text-ink-muted mt-12">
          Admin?{' '}
          <Link to="/login" className="text-sage-700 hover:text-sage-800 underline">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}
